import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Badge, Form, InputGroup, Row, Col, Tabs, Tab, Pagination } from 'react-bootstrap';
import { DeleteModal } from '../../../Common/DeleteModal';
import { Banner, ExtraButton } from '../types';
import { bannerService } from '../api/bannerService';
import { BannerForm } from './BannerForm';
import { BannerPreview } from './BannerPreview';
import { ReactComponent as EditIcon } from '../../../assets/images/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/images/Delete.svg';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import './BannerList.css';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { updateBanner } from '../../../api/bannerManagement';
import { getImageUrl } from '../../../utils/imageUrl';
import { bannerNameManager } from '../utils/bannerNameManager';

interface BannerListProps {
  onEdit: (banner: Banner) => void;
  onPreview: (banner: Banner) => void;
  search: string;
  onSearch: (term: string) => void;
  bannerLocation: 'home' | 'tournament' | 'playerVault';
  platform: 'app' | 'web';
}

export const BannerList: React.FC<BannerListProps> = ({ onEdit, onPreview, search, onSearch, bannerLocation, platform }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewBanner, setPreviewBanner] = useState<Banner | null>(null);
  const [sortField, setSortField] = useState<keyof Banner>('sortNo');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState<'home' | 'tournament' | 'playerVault'>('home');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState(search || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search || '');
  const pageSize = 10;

  // Search options loader for AsyncSelect
  const loadSearchOptions = (inputValue: string, callback: (options: any[]) => void) => {
    // For now, just use the input as the search term
    callback(inputValue ? [{ value: inputValue, label: inputValue }] : []);
  };

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      // For frontend search, we fetch all banners (remove search from API)
      const response = await bannerService.getAllBanners({
        bannerLocation,
        page: currentPage,
        pageSize: pageSize,
        platform,
      });
      console.log('Banner API response:', response);
      
      // Clean up old banner names for deleted banners
      const bannerIds = response.paginatedResults.map((b: Banner) => b.id);
      bannerNameManager.cleanup(bannerIds);
      
      setBanners(response.paginatedResults);
      console.log('Setting banners:', response.paginatedResults);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      setError(null);
    } catch (err) {
      setError('Failed to fetch banners.');
      setBanners([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [bannerLocation, platform, currentPage]);

  // Reset currentPage to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, platform]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleDelete = (banner: Banner) => {
    setSelectedBanner(banner);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedBanner) {
      try {
        await bannerService.deleteBanner(selectedBanner.id);
        fetchBanners(); // Refresh the list after deletion
        setShowDeleteModal(false);
      } catch (err) {
        console.error('Failed to delete banner:', err);
      }
    }
  };

  const handleEdit = (banner: Banner) => {
    setSelectedBanner(banner);
    setShowForm(true);
  };

  const handlePreview = (banner: Banner) => {
    setPreviewBanner(banner);
    setShowPreview(true);
  };

  const handleSort = (field: keyof Banner) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleToggleActive = async (banner: Banner) => {
    const updated = { ...banner, isActive: !banner.isActive };
    setBanners(prev => prev.map(b => b.id === banner.id ? updated : b)); // Optimistic UI
    try {
      const { id, ...rest } = banner;
      await updateBanner({ id: banner.id, payload: { ...rest, isActive: !banner.isActive } });
    } catch (err) {
      setBanners(prev => prev.map(b => b.id === banner.id ? banner : b));
      alert('Failed to update active state.');
    }
  };

  const SortableHeader: React.FC<{ field: keyof Banner; children: React.ReactNode }> = ({ field, children }) => (
    <th
      onClick={() => handleSort(field)}
      style={{ cursor: 'pointer' }}
      className={sortField === field ? 'active' : ''}
    >
      {children}
      {sortField === field && (
        <span className="ms-1">
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </th>
  );

  if (loading) {
    return (
      <>
        <div className="mb-4">
          <Row>
            <Col md={3}>
              <input
                type="search"
                className="form-control"
                placeholder="Search by name or title..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                style={{ minWidth: 180 }}
                aria-label="Search by name or title"
              />
            </Col>
          </Row>
        </div>
        <Table className="custom-table table-nowrap mb-0" style={{ background: '#fff', borderRadius: 12 }}>
          <thead style={{ background: '#E7F5FF' }}>
            <tr>
              <th style={{ 
                fontWeight: 500,
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                fontSize: '14px',
                lineHeight: '16px',
                letterSpacing: '0%'
              }}>#</th>
              <SortableHeader field="title">Title</SortableHeader>
              <th style={{ 
                fontWeight: 500,
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                fontSize: '14px',
                lineHeight: '16px',
                letterSpacing: '0%'
              }}>Platform</th>
              <SortableHeader field="type">Type</SortableHeader>
              <th style={{ 
                fontWeight: 500,
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                fontSize: '14px',
                lineHeight: '16px',
                letterSpacing: '0%'
              }}>Sort</th>
              <th style={{ 
                fontWeight: 500,
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                fontSize: '14px',
                lineHeight: '16px',
                letterSpacing: '0%'
              }}>Active</th>
              <th style={{ 
                fontWeight: 500,
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                fontSize: '14px',
                lineHeight: '16px',
                letterSpacing: '0%'
              }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={7}>
                <ShimmerLoader type="table" rows={5} />
              </td>
            </tr>
          </tbody>
        </Table>
      </>
    );
  }

  const renderBannerTable = () => {
    if (error) {
      return <div className="text-danger">{error}</div>;
    }

    if (!banners || banners.length === 0) {
      return <div className="text-center py-4">No banners found</div>;
    }

    // Apply frontend search filtering
    const filteredBanners = debouncedSearch 
      ? bannerNameManager.searchBanners(banners, debouncedSearch)
      : banners;

    // Sort: active banners first, then inactive
    const sortedBanners = [...filteredBanners].sort((a, b) => {
      const aActive = a.isActive ? 1 : 0;
      const bActive = b.isActive ? 1 : 0;
      return bActive - aActive;
    });

    return (
      <Table className="custom-table table-nowrap mb-0" style={{ background: '#fff', borderRadius: 12 }}>
        <thead style={{ background: '#E7F5FF' }}>
          <tr>
            <th style={{ 
              fontWeight: 500,
              fontFamily: 'Helvetica Neue, Arial, sans-serif',
              fontSize: '14px',
              lineHeight: '16px',
              letterSpacing: '0%'
            }}>#</th>
            <SortableHeader field="title">Title</SortableHeader>
            <th style={{ 
              fontWeight: 500,
              fontFamily: 'Helvetica Neue, Arial, sans-serif',
              fontSize: '14px',
              lineHeight: '16px',
              letterSpacing: '0%'
            }}>Platform</th>
            <SortableHeader field="type">Type</SortableHeader>
            <th style={{ 
              fontWeight: 500,
              fontFamily: 'Helvetica Neue, Arial, sans-serif',
              fontSize: '14px',
              lineHeight: '16px',
              letterSpacing: '0%'
            }}>Sort</th>
            <th style={{ 
              fontWeight: 500,
              fontFamily: 'Helvetica Neue, Arial, sans-serif',
              fontSize: '14px',
              lineHeight: '16px',
              letterSpacing: '0%'
            }}>Active</th>
            <th style={{ 
              fontWeight: 500,
              fontFamily: 'Helvetica Neue, Arial, sans-serif',
              fontSize: '14px',
              lineHeight: '16px',
              letterSpacing: '0%'
            }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedBanners.map((banner, idx) => {
            console.log('Banner extraButtons:', banner.extraButtons, banner);
            return (
              <tr key={banner.id}>
                <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                <td>{bannerNameManager.getBannerDisplayName(banner)}</td>
                <td>
                  <span className={`custom-badge custom-badge-${banner.platform === 'web' ? 'blue' : 'gray'}`}>{banner.platform === 'web' ? 'Web' : 'App'}</span>
                </td>
                <td>
                  {banner.extraButtons && banner.extraButtons.length > 0 ? (
                    banner.extraButtons.map((btn: ExtraButton, i: number) => {
                      if (btn.category === 'button') {
                        // Prefer redirectionType, fallback to redirection.type
                        const type = (btn as any).redirectionType || (btn as any).redirection?.type || 'None';
                        const label = type.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase()).trim();
                        return (
                          <span key={i} className="custom-badge custom-badge-outline custom-badge-blue">{label}</span>
                        );
                      } else if (btn.category === 'share') {
                        return (
                          <span key={i} className="custom-badge custom-badge-outline custom-badge-purple">Share</span>
                        );
                      } else {
                        return (
                          <span key={i} className="custom-badge custom-badge-outline custom-badge-gray">None</span>
                        );
                      }
                    })
                  ) : (
                    <span className="custom-badge custom-badge-outline custom-badge-gray">None</span>
                  )}
                </td>
                <td>{banner.sortNo}</td>
                <td>
                  <Form.Check
                    type="switch"
                    id={`isActive-switch-${banner.id}`}
                    checked={!!banner.isActive}
                    onChange={() => handleToggleActive(banner)}
                    label=""
                  />
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <button
                      className="icon-btn edit-btn"
                      onClick={() => handleEdit(banner)}
                      title="Edit"
                      type="button"
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="icon-btn delete-btn"
                      onClick={() => handleDelete(banner)}
                      title="Delete"
                      type="button"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <>
      <div className="mb-4">
        <Row>
          <Col md={3}>
            <input
              type="search"
              className="form-control"
              placeholder="Search by title..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{ minWidth: 180 }}
              aria-label="Search by title"
            />
          </Col>
        </Row>
      </div>
      {renderBannerTable()}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, idx) => (
              <Pagination.Item
                key={idx + 1}
                active={currentPage === idx + 1}
                onClick={() => handlePageChange(idx + 1)}
              >
                {idx + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        deleteModalFunction={handleDeleteConfirm}
        title="Delete Banner"
        message="Are you sure you want to delete this banner? This action cannot be undone."
      />
      <BannerForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setSelectedBanner(null);
        }}
        banner={selectedBanner}
        onSuccess={fetchBanners}
      />
      <BannerPreview
        show={showPreview}
        onHide={() => {
          setShowPreview(false);
          setPreviewBanner(null);
        }}
        banner={previewBanner}
        imageUrl={getImageUrl(previewBanner?.image)}
      />
    </>
  );
}; 